import { Council } from ".";
import { IUser } from "./user.type";
import { Language } from "./language.types";

// Education Institution Types
export interface EducationInstitutionDTO {
    id: string;
    name: string;
    code: number; // Institution code
    type: string; // Institution type enum - DEPRECATED, use types instead
    types: string[]; // 🆕 Array of institution type enums
    country: string;
    city: string;
    address: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    website: string | null;
    description: string | null;
    councilId: string;
    councilName: string; // Read-only
    isActive: boolean;
    enabled: boolean;
    establishedYear: number;
    accreditationStatus: string | null;
    programs?: EducationProgram[]; // Optional, included in specific endpoints
    createdAt: string; // Read-only
    updatedAt: string; // Read-only
}

export interface CreateEducationInstitutionRequest {
    name: string;
    code: number; // Institution code
    type?: string; // DEPRECATED - use types instead
    types: string[]; // 🆕 Array of institution type enums
    country: string;
    city: string;
    address: string | null;
    contactEmail: string | null;
    contactPhone: string | null;
    website: string | null;
    description: string | null;
    councilId: string;
    establishedYear: number;
    accreditationStatus: string | null;
    isActive: boolean;
    programIds: string[]; // Array of education program IDs
}

export interface UpdateEducationInstitutionRequest extends CreateEducationInstitutionRequest {
    id: string;
}

export interface EducationInstitutionFilters {
    active?: boolean;
    enabled?: boolean;
    country?: string;
    type?: string; // Filter by any institution type (searches in types array)
    name?: string;
    hasType?: string; // 🆕 Filter institutions that have a specific type in their types array
}

export interface EducationInstitutionStatusResponse {
    institutionId: string;
    hasActivePrograms: boolean;
    status: 'HAS_ACTIVE_PROGRAMS' | 'NO_ACTIVE_PROGRAMS';
    message: string;
}

// 🆕 Institution Summary DTO for enhanced exam schedule responses
export interface InstitutionSummaryDTO {
    id: string;
    name: string;
    types: string[];
    councilId: string;
}

export interface QuestionCategory {
    id: string;
    name: string;
    code: string;
    description?: string;
    active: boolean;
    councilId: string;
    councilName?: string;
    educationLevel: string;
    createdAt: string;
    updatedAt: string;
}

// Grading Criteria interface for Education Programs
export interface GradingCriteria {
    passing_percentage: number;        // Overall passing percentage (e.g., 60.00)
    min_theory_percentage: number;     // Minimum theory percentage (e.g., 40.00) - renamed from min_session_percentage
    min_practical_percentage: number;  // Minimum practical percentage (e.g., 50.00)
    theory_weight: number;             // Weight for theory component (0.0-1.0, e.g., 0.40)
    practical_weight: number;          // Weight for practical component (0.0-1.0, e.g., 0.60)
    allow_average_exception: boolean;  // Whether to allow manual approval for students meeting minimums but failing overall
    // Deprecated field - kept for backward compatibility
    requires_both_pass?: boolean;     // DEPRECATED: No longer used in weighted scoring
}

// New Education Program interface (replaces QuestionCategory)
export interface EducationProgram {
    id: string;
    name: string;
    code: string;
    description?: string;
    educationLevel: string;
    active: boolean;
    councilId: string;
    councilName: string;
    supportsPracticalExams?: boolean;  // 🆕 New field
    gradingCriteria?: GradingCriteria; // 🆕 Updated to use structured interface
    linkedInstitutions?: InstitutionSummaryDTO[]; // 🆕 Linked institutions
    institutionCount?: number; // 🆕 Count of linked institutions
    createdAt: string;
    updatedAt: string;
}

export interface CreateQuestionCategoryRequest {
    name: string;
    code: string;
    description?: string;
    councilId: string;
    educationLevel: string;
    active?: boolean;
}

// New Education Program request interfaces
export interface CreateEducationProgramRequest {
    name: string;                    // Required
    code: string;                   // Required, max 10 chars  
    description?: string;
    educationLevel: string;         // Required
    isActive: boolean;             // Default: true
    councilId: string;             // Required
    supportsPracticalExams?: boolean; // 🆕 New field for practical exam support
    gradingCriteria?: GradingCriteria; // 🆕 Custom grading criteria for this program
    institutionIds?: string[];     // 🆕 Institution IDs that offer this program
}

export interface UpdateQuestionCategoryRequest extends CreateQuestionCategoryRequest {
    id: string;
}

export interface UpdateEducationProgramRequest extends CreateEducationProgramRequest {
    id: string;
}

export interface QuestionCategoryFilters {
    councilId?: string;
    active?: boolean;
    search?: string;
}

export interface EducationProgramFilters {
    councilId?: string;
    isActive?: boolean;
    search?: string;
}

export interface QuestionModule {
    id: string;
    name: string;
    code: string;
    description?: string;
    active: boolean;
    educationProgramId: string;  // 🔄 Changed from categoryId
    educationProgramName?: string;  // 🔄 Changed from categoryName
    councilId: string;
    councilName?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateQuestionModuleRequest {
    name: string;
    code: string;
    description?: string;
    educationProgramId: string;  // 🔄 Changed from categoryId
    councilId: string;
    active?: boolean;
}

export interface UpdateQuestionModuleRequest extends CreateQuestionModuleRequest {
    id: string;
}

export interface QuestionUnit {
    id: string;
    name: string;
    code: string;
    description?: string;
    active: boolean;
    moduleId: string;
    moduleName?: string;
    councilId: string;
    councilName?: string;
    bloomsTaxonomyLevelIds?: string[];
    bloomsLevels?: BloomsTaxonomyLevel[];
    createdAt: string;
    updatedAt: string;
}

export interface CreateQuestionUnitRequest {
    name: string;
    code: string;
    description?: string;
    moduleId: string;
    councilId: string;
    active?: boolean;
    bloomsLevelIds?: string[];
}

export interface UpdateQuestionUnitRequest extends CreateQuestionUnitRequest {
    id: string;
}

export interface BloomsTaxonomyLevel {
    id: string;
    level: number;
    name: string;
    code: string;
    description?: string;
    keywords?: string;
    active: boolean;
    councilId: string;
    councilName?: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateBloomsTaxonomyLevelRequest {
    level: number;
    name: string;
    code: string;
    description?: string;
    keywords?: string;
    councilId: string;
    active?: boolean;
}

export interface UpdateBloomsTaxonomyLevelRequest extends CreateBloomsTaxonomyLevelRequest {
    id: string;
}

export interface Question {
    id: string;
    questionBody?: string;  // 🆕 Question text from backend
    questionNumber?: number;  // 🆕 Question number from backend
    options: QuestionOption[];
    council?: Council;
    councilId?: string;
    councilName?: string;
    educationProgramId?: string;  // 🔄 Changed from categoryId
    educationProgramName?: string;  // 🔄 Changed from categoryName
    educationProgramCode?: string;  // 🔄 Changed from categoryCode
    moduleId?: string;
    moduleName?: string;
    moduleCode?: string;
    unitId?: string;
    unitName?: string;
    unitCode?: string;
    bloomsLevelIds?: string[];
    bloomsLevels?: BloomsTaxonomyLevel[];
    bloomsLevelNames?: string[];
    bloomsLevelCodes?: string[];
    bloomsLevelNumbers?: number[];
    caseScenario?: string;
    keywords?: string;
    creditWeight?: number;
    difficultyLevel?: string;
    explanation?: string;
    // Backend returns questionMediaPath, not mediaPath
    questionMediaPath?: string;
    mediaPath?: string; // Keep for backward compatibility
    mediaInfo?: {
        originalFilename: string;
        contentType: string;
        size: number;
        uploadedAt: string;
    };
    // Translation support - EN is always required
    translations: Record<string, string>;
    currentLanguage?: string;
    availableLanguages?: string[];
    createdAt?: string;
    updatedAt?: string;
}

export interface QuestionOption {
    id: string;
    questionId?: string;
    correct?: boolean;
    // Backend returns questionOptionBody as the default text
    questionOptionBody?: string;
    // Translation support - EN is always required
    translations: Record<string, string>;
    currentLanguage?: string;
    createdAt?: string;
    updatedAt?: string;
}


export interface QuestionAction {
    id: string;
    questionId: string;
    questionBody: string;
    actionType: string;
    reason: string;
    authorId: string;
    authorName: string;
    authorEmail: string;
    actionTimestamp: string;
    oldData?: string;
    newData?: string;
    additionalData?: string;
}


export interface BloomsDistribution {
    [level: number]: number;
}

export interface ModuleSelection {
    moduleId: string;
    questionCount: number;
    bloomsDistribution?: BloomsDistribution;
}

export interface UnitSelection {
    unitId: string;
    moduleId?: string;
    questionCount: number;
    bloomsDistribution?: BloomsDistribution;
}

export interface QuestionSelection {
    totalSeats?: number;
    moduleSelections?: ModuleSelection[];
    unitSelections?: UnitSelection[];
}


export interface QuestionExportFilters {
    councilId?: string;
    educationProgramId?: string;  // 🔄 Changed from categoryId
    moduleId?: string;
    unitId?: string;
    startDate?: string;
    endDate?: string;
    authorId?: string;
}

export interface QuestionActionExportFilters {
    questionId?: string;
    actionType?: string;
    authorId?: string;
    startDate?: string;
    endDate?: string;
}


export interface ExamSchedule {
    id: string;
    title: string;
    startDate: string;
    startTime: string;
    endTime?: string;
    totalQuestions: number;
    totalSeats: number;
    remainingSeats: number;
    duration: number;
    examSubCenterIds?: string[]; // Legacy field - kept for backward compatibility
    examCenterIds?: string[]; // New field for center IDs
    examCenters?: ExamCenter[];
    examSubCenters?: ExamSubCenterWithInvigilator[];
    examCenterSelection?: ExamCenterSelectionConfig;
    isPublished: boolean;
    public: boolean;
    allowedInstitutions?: string[]; // UUIDs of institutions allowed to access private exam
    retakeSeatsAllocated?: number;    // 🆕 Number of seats allocated for retake attempts
    retakeSeatsUsed?: number;         // 🆕 Current number of retake seats in use
    retakeSeatsAvailable?: number;    // 🆕 Calculated field (allocated - used)
    canAccommodateRetakes?: boolean;  // 🆕 Whether retake seats are available
    accessType?: 'PUBLIC' | 'PRIVATE'; // 🆕 Access type indicator
    institutionAccessCount?: number;  // 🆕 Number of institutions with access
    councilId: string;
    councilName?: string;
    council?: Council;
    questions?: Question[];
    questionSelection?: QuestionSelectionRequestDTO; // Enhanced question selection display for manual exams
    supportedLanguageIds?: string[]; // Languages supported for this exam (for backward compatibility)
    supportedLanguages?: string[]; // Language codes supported for this exam (e.g. ["EN", "FR"])
    createdAt: string;
    updatedAt: string;
    published?: boolean;
    categoryId?: string;              // 🔄 Keep for backward compatibility
    category?: QuestionCategory;      // 🔄 Keep for backward compatibility
    educationProgramId?: string;      // 🆕 New field
    educationProgram?: EducationProgram; // 🆕 New field
    locationSummary?: string;
    manuallyCreated?: boolean;
    requiredPracticalExam?: boolean;  // 🆕 Whether practical exam is required
}

// Types for ScheduleInformationStep components
export interface LocalExamCenterSelection {
    examCenterId: string;
    examCenterName: string;
}

// 🆕 New interfaces for subcenter selection
export interface ExamCenterSubCenterSelection {
    examCenterId: string;
    examCenterName: string;
    selectedSubCenterIds: string[];
}

export interface ExamCenterSubCenterSelectionConfig {
    examCenterSubCenterSelections: ExamCenterSubCenterSelection[];
}

export interface ScheduleFormValues {
    title: string;
    startDate: string;
    startTime: string;
    duration: number;
    published: boolean;
    educationProgramId: string;
    supportedLanguageIds: string[];
    questionSelection: QuestionSelection;
    examCenterSelection: ExamCenterSelectionConfig;
    // 🆕 New subcenter selection structure
    examCenterSubCenterSelection: ExamCenterSubCenterSelectionConfig;
    // 🆕 Enhanced fields
    public: boolean;
    allowedInstitutionIds: string[];
    retakeSeatsAllocated: number;
}

export interface EducationProgramOption {
    id: string;
    code: string;
    name: string;
    educationLevel: string;
}

// 🆕 Subcenter option for UI selection
export interface SubCenterOption {
    id: string;
    name: string;
    seats: number;
    currentOccupancy?: number;
    remainingSeats?: number;
    active: boolean;
}

export interface LanguageOption {
    id: string;
    code: string;
    name: string;
    enabled: boolean;
}

export interface ExamCenterOption {
    id: string;
    name: string;
    code: number;
    subCenters?: Array<{
        id: string;
        name: string;
        seats: number;
    }>;
}

export interface ExamScheduleFilters {
    councilId?: string;
    isPublished?: boolean;
    public?: boolean;
    isPrivate?: boolean;
    includePublic?: boolean;
    startDate?: string;
    endDate?: string;
    location?: string;
}


export interface ExamCenterSelection {
    id?: string;
    label?: string;
    examCenterId: string;
    examCenterName?: string;
    subCenterSelections: ExamSubCenterSelection[];
}

export interface ExamSubCenterSelection {
    examSubCenterId: string;
    examSubCenterName?: string;
    seats: number;
}

export interface ExamCenterSelectionConfig {
    examCenterSelections: ExamCenterSelection[];
}

export interface CreateExamScheduleRequest {
    title: string;
    startDate: string;
    startTime: string;
    duration: number;
    examCenterSubCenters: Record<string, string[]>; // 🆕 Center ID mapped to subcenter IDs
    councilId: string;
    educationProgramId: string;       // 🔄 Changed from categoryId
    published: boolean;
    public?: boolean;
    allowedInstitutionIds?: string[]; // 🆕 Institution IDs allowed to access private exam
    retakeSeatsAllocated?: number;    // 🆕 Number of seats allocated for retake attempts
    supportedLanguageIds?: string[]; // Languages supported for this exam
    questionSelection?: QuestionSelection;
    // Legacy field for backward compatibility during transition
    examCenterIds?: string[]; // ❌ DEPRECATED: Use examCenterSubCenters instead
}

export interface UpdateExamScheduleRequest extends Omit<CreateExamScheduleRequest, 'published'> {
    id: string;
    published?: boolean;
    isPublished?: boolean;
}


export interface DefaultExamFormatUnit {
    unitId: string;
    unitName: string;
    unitCode: string;
    bloomLevelsCount: number;
    questionsCount: number;
    bloomsLevels: BloomsTaxonomyLevel[];
}

export interface DefaultExamFormatModule {
    moduleId: string;
    moduleName: string;
    moduleCode: string;
    totalBloomLevels: number;
    units: DefaultExamFormatUnit[];
}

export interface DefaultExamFormat {
    educationProgramId: string;  // 🔄 Changed from categoryId
    educationProgramName: string;  // 🔄 Changed from categoryName
    educationProgramCode: string;  // 🔄 Changed from categoryCode
    educationLevel: string;
    totalQuestions: number;
    modules: DefaultExamFormatModule[];
    councilId: string;
    councilName: string;
}

export interface ExamSchedulePublicDTO {
    id: string;
    title: string;
    startDate: string;
    startTime: string;
    endTime: string;
    totalQuestions: number;
    published: boolean;
    duration: number;
    examCenters?: ExamCenter[];
    examSubCenters?: ExamSubCenter[];
    council?: Council;
    councilId: string;
    councilName: string;
    locationSummary?: string;
    category?: QuestionCategory;              // 🔄 Keep for backward compatibility
    educationProgram?: EducationProgram;      // 🆕 New field
    supportedLanguages?: string[]; // Language codes supported for this exam (e.g. ["EN", "FR"])
    allowedInstitutions?: string[]; // UUIDs of institutions allowed to access private exam
    manuallyCreated?: boolean;
    retakeSeatsAllocated?: number;    // 🆕 Number of seats allocated for retake attempts
    retakeSeatsUsed?: number;         // 🆕 Current number of retake seats in use
    retakeSeatsAvailable?: number;    // 🆕 Calculated field (allocated - used)
    questionSelection?: QuestionSelectionRequestDTO; // 🆕 Question selection details (only for EXAMINER on manual exams)
    centerAssigned?: boolean;         // 🆕 Whether exam center has been assigned
    createdAt: string;
    updatedAt: string;
}

// 🆕 Seat availability DTO for dedicated endpoint
export interface SeatAvailabilitySubCenterBreakdown {
    subCenterId: string;
    subCenterName: string;
    totalSeats: number;
    remainingSeats: number;
    activeSessions: number;
}

export interface SeatAvailabilityCenterBreakdown {
    centerId: string;
    centerName: string;
    totalSeats: number;
    remainingSeats: number;
    subCenters: SeatAvailabilitySubCenterBreakdown[];
}

export interface SeatAvailabilityResponse {
    examScheduleId: string;
    examDate: string;
    totalSeats: number;
    remainingSeats: number;
    occupiedSeats: number;
    occupancyRate: number;
    centerBreakdowns: SeatAvailabilityCenterBreakdown[];
}

export interface ExamAnalytics {
    examScheduleId: string;
    examTitle: string;
    totalRegistrations: number;
    totalSeats: number;
    remainingSeats: number;
    completedSessions: number;
    ongoingSessions: number;
    blockedSessions: number;
    averageScore: number;
    passRate: number;
    averageDuration: number;
    questionAnalytics: QuestionAnalytics[];
    scoreDistribution: ScoreDistribution;
    examScheduleInfo?: {
        id: string;
        title: string;
        startDate: string;
        startTime: string;
        duration: number;
        location: string;
        totalSeats: number;
        totalQuestions: number;
        council: {
            id: string;
            councilName: string;
        };
    };
    registrationMetrics?: {
        totalRegistered: number;
        totalAttended: number;
        totalCompleted: number;
        noShows: number;
        registrationTrend: Array<{
            date: string;
            registrations: number;
            cumulativeRegistrations: number;
        }>;
    };
    performanceMetrics?: {
        averageScore: number;
        medianScore: number;
        highestScore: number;
        lowestScore: number;
        standardDeviation: number;
        passRate: number;
        failRate: number;
        totalPassed: number;
        totalFailed: number;
        scoreDistribution: Array<{
            range: string;
            count: number;
            percentage: number;
        }>;
    };
    timeMetrics?: {
        averageCompletionTime: number;
        medianCompletionTime: number;
        fastestCompletion: number;
        slowestCompletion: number;
        completionTimeDistribution: Array<{
            timeRange: string;
            count: number;
        }>;
    };
    sessionMetrics?: {
        totalSessions: number;
        activeSessions: number;
        completedSessions: number;
        expiredSessions: number;
        blockedSessions: number;
        suspendedSessions: number;
        sessionStatusBreakdown: Array<{
            status: string;
            count: number;
            percentage: number;
        }>;
    };
    appealMetrics?: {
        totalAppeals: number;
        pendingAppeals: number;
        approvedAppeals: number;
        rejectedAppeals: number;
        appealRate: number;
        averageAppealScore: number;
        appealOutcomes: Array<{
            outcome: string;
            count: number;
        }>;
    };
    retakeMetrics?: {
        totalRetakeRequests: number;
        approvedRetakes: number;
        pendingRetakes: number;
        rejectedRetakes: number;
        retakeRate: number;
    };
}

export interface QuestionAnalytics {
    questionId: string;
    questionBody: string;
    correctAnswers: number;
    totalAnswers: number;
    accuracy: number;
    averageTimeSpent: number;
    totalAnswered?: number;
    incorrectAnswers?: number;
    accuracyRate?: number;
    optionBreakdown?: Array<{
        optionId: string;
        optionText: string;
        selectedCount: number;
        percentage: number;
        isCorrect: boolean;
    }>;
}

export interface ScoreDistribution {
    [range: string]: number;
}


export interface ExamSession {
    id: string;
    examSchedule: ExamSchedule;
    user: IUser;
    examCode: string;
    status?: string; // PENDING, ACTIVE, ENDED, EXPIRED, BLOCKED
    startTime?: string;
    endTime?: string;
    
    // Location fields
    examCenterId?: string;
    examSubCenterId?: string;
    examCenterName?: string;
    examSubCenterName?: string;
    
    // Device verification fields
    deviceVerified: boolean;
    deviceVerifiedAt?: string;
    verifiedDeviceInfo?: string;
    verificationAttempts: number;
    maxVerificationAttempts: number;
    
    // Attendance verification fields
    attendanceVerified: boolean;
    attendanceVerifiedById?: string;
    attendanceVerifiedByEmail?: string;
    attendanceVerifiedAt?: string;
    
    // Language fields
    language?: ExamSessionLanguage;
}

export interface ExamSessionDTO {
    id: string;
    examScheduleId: string;
    examScheduleName: string;
    examSchedule: {
        id: string;
        title: string;
        startDate: string;
        startTime: string;
        totalQuestions: number;
        duration: number;
        location: string;
        council: Council;
        published: boolean;
        totalSeats: number;
        remainingSeats: number;
        supportedLanguages?: Array<{
            id: string;
            code: string;
            name: string;
        }>;
    };
    userId: string;
    userEmail: string;
    user?: {
        fullName: string;
        firstName: string;
        lastName: string;
        email: string;
    };
    examCode: string;
    ipAddress: string;
    deviceInfo: string;
    status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    startedAt: string | null;
    endedAt: string | null;
    additionalTime?: number;
    calculatedEndTime?: string;
    remainingTime?: number | null;
    autoSubmitted?: boolean;
    educationProgram?: {
        name: string;
        supportsPracticalExams: boolean;
    };
    council?: {
        name: string;
    };
    
    // Location fields
    examCenterId?: string;
    examSubCenterId?: string;
    examCenterName?: string;
    examSubCenterName?: string;
    examSubCenter?: ExamSubCenter; // 🆕 Full sub-center information
    
    // Device verification fields
    deviceVerified: boolean;
    deviceVerifiedAt?: string;
    verifiedDeviceInfo?: string;
    verificationAttempts: number;
    maxVerificationAttempts: number;
    
    // Attendance verification fields
    attendanceVerified: boolean;
    attendanceVerifiedById?: string;
    attendanceVerifiedByEmail?: string;
    attendanceVerifiedAt?: string;
    
    // Language fields
    language?: ExamSessionLanguage;
    
    actions?: ExamSessionAction[];
    createdAt: string;
    updatedAt: string;
    
    // Exam result (when available)
    examResult?: ExamResultDTO;
    
    // Practical exam result (when available)
    practicalExamResult?: PracticalExamResultDTO;

    educationInstitution?: EducationInstitution;
    
    // 🆕 New fields for EXPIRED session grace period handling
    requiresDirectSubmission?: boolean; // true ONLY when session is EXPIRED AND within grace period
    isWithinGracePeriod?: boolean; // true if within 10-minute grace period after exam end
    lastResumedAt?: string; // timestamp when session was last batch-resumed (5-minute expiry)
}

// ============================================================================
// NEW BACKEND DTOs - Optimized Response Structures
// ============================================================================

// POST /api/v1/exam-sessions - Create Exam Session Response
export interface ExamSessionCreateResponse {
    sessionId: string;
    examCode: string;
    status: 'PENDING';
    subCenter: { id: string; name: string };
    center: { id: string; name: string; location: string };
}

// GET /api/v1/exam-sessions/{id} - Get Exam Session Detail Response
export interface ExamSessionDetailResponse {
    id: string;
    examCode: string;
    status: 'NOT_STARTED' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    ipAddress?: string | null;
    deviceInfo?: string;
    autoSubmitted?: boolean;
    deviceVerified: boolean;
    deviceVerifiedAt?: string | null;
    verifiedDeviceInfo?: string | null;
    verificationAttempts?: number;
    maxVerificationAttempts: number;
    attendanceVerified: boolean;
    attendanceVerifiedById?: string | null;
    attendanceVerifiedByEmail?: string | null;
    attendanceVerifiedAt?: string | null;
    requiresDirectSubmission: boolean;
    isWithinGracePeriod: boolean;
    gracePeriodMinutes: number;
    startedAt: string | null;
    endedAt: string | null;
    additionalTime: number;
    calculatedEndTime?: string | null;
    remainingTime?: number | null;
    createdAt: string;
    updatedAt?: string;
    snapshotCouncilId: string;
    user: {
        id: string;
        nationalID: string | null;
        firstName: string;
        lastName: string;
        email: string;
    };
    examSchedule: {
        id: string;
        title: string;
        startDate: string;
        startTime: string;
        duration: number;
        totalQuestions: number | null;
        councilId: string;
        councilName: string | null;
    };
    examSubCenter: {
        id: string;
        name: string;
        examCenterId: string;
        examCenterName: string;
    };
    language: {
        id: string;
        code: string;
        name: string | null;
        nativeName: string | null;
    };
}

// POST /api/v1/exam-sessions/{id}/extend-time, mark-attendance, update-language - Action Response
export interface ExamSessionActionResponse {
    id: string;
    status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    // For extend-time
    additionalTime?: number;
    calculatedEndTime?: string;
    // For mark-attendance
    attendanceVerified?: boolean;
    attendanceVerifiedByEmail?: string;
    attendanceVerifiedAt?: string;
    // For update-language
    languageId?: string;
    languageCode?: string;
    languageName?: string;
}

// GET /api/v1/exam-sessions/exam-schedule/{examScheduleId} - List Item DTO
export interface ExamSessionListItemDTO {
    id: string;
    examCode: string;
    status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    deviceVerified: boolean;
    attendanceVerified: boolean;
    startedAt: string | null;
    endedAt: string | null;
    additionalTime: number;
    userId: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    subCenterId: string;
    subCenterName: string;
}

// GET /api/v1/exam-sessions/user/{userId} - History Item DTO
export interface ExamSessionHistoryItemDTO {
    id: string;
    examCode: string;
    status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    startedAt: string | null;
    endedAt: string | null;
    calculatedEndTime?: string | null;
    remainingTime?: number | null;
    createdAt: string;
    examScheduleId: string;
    examScheduleTitle: string;
    languageCode: string;
    languageName: string;
}

// GET /api/v1/exam-sessions/sub-center/{subCenterId} - Room Item DTO
export interface ExamSessionRoomItemDTO {
    id: string;
    examCode: string;
    status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    deviceVerified: boolean;
    attendanceVerified: boolean;
    startedAt: string | null;
    endedAt: string | null;
    additionalTime: number;
    userId: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    userNationalId?: string | null;
    examScheduleId: string;
    examScheduleTitle: string;
    languageCode: string;
}

// GET /api/v1/exam-sessions/exam-code/{examCode} - Verify Response
export interface ExamSessionVerifyResponse {
    id: string;
    examCode: string;
    status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    deviceVerified: boolean;
    attendanceVerified: boolean;
    userId: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    userNationalId?: string | null;
    userPhoneNumber?: string;
    userDateOfBirth?: string;
    userGender?: 'MALE' | 'FEMALE';
    userCitizenType?: 'CITIZEN' | 'FOREIGNER';
    educationInstitutionId?: string;
    examScheduleId?: string;
    languageId?: string;
    examCenterId?: string;
    gracePeriodMinutes: number;
    maxVerificationAttempts: number;
    languageCode: string;
    languageName?: string | null;
}

// Enhanced verification response interface
export interface VerifyExamSessionResponse {
    examSession: ExamSessionDTO; // Changed from examSessionDTO to examSession
    token?: string; // JWT token for LOGIN type
    user?: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
    };
    roles?: Array<{
        id: string;
        roleName: string;
    }>;
}

export interface ActiveExamScheduleDTO {
    id: string;
    title: string;
    startDate: string;
    startTime: string | { hour: number; minute: number; second: number; nano: number };
    duration: number;
    additionalTime: number;
    examSessionId: string;
    examCode: string;
    remainingTime?: number | null;
    calculatedStartTime?: string;
    calculatedEndTime?: string;
    questions: Array<{
        id: string;
        questionText: string;
        questionType: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'SHORT_ANSWER';
        options: string[];
        marks: number;
    }>;
    // Removed nested examSession object
}

export interface SubmitAnswerRequest {
    questionId: string;
    optionId: string;
}

export interface SubmitAllAnswersRequest {
    answers: SubmitAnswerRequest[];
    reason?: string;
}

export interface SubmitAllAnswersWithReasonRequest {
    answers: SubmitAnswerRequest[];
    reason?: string;
    autoSubmitted?: boolean;
}

// Exam Card Types
export interface ExamCardData {
    firstName: string;
    lastName: string;
    nationalId: string;
    email: string;
    educationProgramName: string;
    educationProgramLevel: string;
    educationInstitutionName: string;
    examTitle: string;
    examCode: string;
    examDate: string;
    examTime: string;
    duration: number;
    language: string;
    examCenterName: string;
    examSubCenterName: string;
    examCenterLocation: string;
    councilName: string;
    councilEmail: string;
    generatedAt: string;
}

export interface ExamCardDownloadRequest {
    sessionId: string;
    filename?: string;
}

export interface ExamCardQRCodeRequest {
    examCode: string;
    userId?: string;
}

export interface SubmittedAnswerDTO {
    id: string;
    questionId: string;
    optionId: string;
    submittedAt: string;
    updatedAt: string;
}

// Verification request interface
export interface VerifyExamSessionRequest {
    nationalId: string;
    examCode: string;
    type?: "VERIFY" | "LOGIN"; // Optional: "VERIFY" (default) or "LOGIN"
}

// Attendance marking request interface
export interface MarkAttendanceRequest {
    reason?: string;
    comments?: string;
}


export interface ExamResultDTO {
    id: string;
    examSessionId: string;
    examSessionCode: string;
    examCode?: string;
    examScheduleTitle: string;
    examTitle: string;
    userId: string;
    userEmail: string;
    userName: string;
    totalQuestions: number;
    correctAnswers: number;
    scorePercentage: number;
    published: boolean;
    computedAt: string;
    recordedAt: string;
    recordedBy: string;
    recordedByName: string;
    notes?: string;
    educationProgramId: string;
    educationProgramName: string;
    councilId: string;
    councilName: string;
    
    // 🆕 Enhanced Unified Exam Result Fields
    isFinalScore?: boolean;           // TRUE if this is the final combined score, FALSE if awaiting practical exam
    sessionScorePercentage?: number;  // Written exam score - Always available after session completion
    finalScorePercentage?: number;    // Combined score - Only available after practical exam completion
    awaitingPracticalExam?: boolean;  // TRUE if practical exam results are still pending
    finalPublished?: boolean;         // TRUE if final results have been published
    passed?: boolean;                 // Final pass/fail status - Only available when finalPublished is true
    requiresPracticalExam?: boolean;  // TRUE if exam schedule requires practical exam
    practicalExamResult?: PracticalExamResultDTO; // Practical exam details when available
    
    // 🆕 Practical Exam Support Fields
    theoryScore?: number;             // Theory component score
    practicalScore?: number;          // Practical component score
    finalScore?: number;              // Final weighted score
    grade?: string;                   // Grade classification (PASS/MERIT/DISTINCTION)
    gradingDetails?: {
        appliedCriteria?: string;     // Applied grading criteria description
        theoryWeight?: number;        // Weight of theory component
        practicalWeight?: number;     // Weight of practical component
        calculation?: string;         // Grade calculation explanation
        gradeBand?: {
            grade: string;
            range: string;
            description: string;
        };
        componentResults?: {
            theory: {
                score: number;
                passed: boolean;
                requirement: string;
            };
            practical: {
                score: number;
                passed: boolean;
                requirement: string;
            };
        };
    };
    
    // 🆕 Manual Approval Fields
    pendingManualApproval?: boolean;    // TRUE if result is pending manual approval
    manualApprovalBy?: string;          // UUID of admin who approved/rejected (null if not processed)
    manualApprovalAt?: string;          // Timestamp of approval/rejection (null if not processed)
    manualApprovalReason?: string;      // Reason for manual approval (e.g., "AVERAGE_EXCEPTION")
    manualApprovalNotes?: string;       // Admin notes for approval/rejection decision
    originalScorePercentage?: number;   // Original score before manual entry override (for manual entry conflicts)
    
    // 🆕 Updated Grading Criteria (Weighted Scoring)
    gradingCriteria?: {
        passing_percentage: number;
        min_theory_percentage: number;     // Renamed from min_session_percentage
        min_practical_percentage: number;
        theory_weight: number;             // Weight for theory component (0.0-1.0)
        practical_weight: number;          // Weight for practical component (0.0-1.0)
        allow_average_exception: boolean;  // Whether to allow manual approval exceptions
        // Deprecated field - kept for backward compatibility
        requires_both_pass?: boolean;     // DEPRECATED: No longer used in weighted scoring
    };
    
    // 🆕 Batch Processing Status Fields
    computationStatus?: ComputationStatus;  // PENDING, PROCESSING, COMPLETED, FAILED
    processing?: boolean;                    // true if result is still being computed (PENDING or PROCESSING)
    
    createdAt: string;
    updatedAt: string;
}


export enum AppealType {
    THEORY = 'THEORY',
    PRACTICAL = 'PRACTICAL'
}

export enum EAppealStatus {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED'
}

// 🆕 Batch Processing Status Enum
export enum ComputationStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED'
}

export enum EOutcomeImpact {
    RESULT_UPDATED = 'RESULT_UPDATED',
    RETAKE_GRANTED = 'RETAKE_GRANTED',
    NO_CHANGE = 'NO_CHANGE'
}

export interface ExamAppealDTO {
    id: string;
    appealType: AppealType;
    examResultId: string | null; // For theory appeals
    practicalExamResultId: string | null; // For practical appeals
    userId: string;
    userEmail: string;
    userName: string;
    examCode: string;
    examTitle: string;
    originalScore: number;
    score?: number | null; // New score after appeal review
    totalQuestions?: number | null; // Total questions (only for theory appeals)
    appealReason: string;
    supportingDocumentPath?: string | null;
    submissionDate: string;
    appealStatus: EAppealStatus;
    reviewedById?: string | null;
    reviewedByEmail?: string | null;
    reviewDate?: string | null;
    reviewNotes?: string | null;
    outcomeImpact?: EOutcomeImpact | null;
    appealDeadline: string;
    isExpired: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface SubmitAppealRequest {
    examResultId?: string; // For theory appeals (XOR with practicalExamResultId)
    practicalExamResultId?: string; // For practical appeals (XOR with examResultId)
    appealReason: string;
    supportingDocument?: File;
}

export interface ReviewAppealRequest {
    appealStatus: EAppealStatus;
    reviewNotes?: string;
    outcomeImpact?: EOutcomeImpact;
    updatedScore?: number;
}

export interface AppealEligibilityResponse {
    canAppeal: boolean;
    existingAppeal?: ExamAppealDTO;
}


export enum EAttemptStatus {
    PENDING = 'PENDING',
    PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',
    PAYMENT_PENDING = 'PAYMENT_PENDING',
    PAYMENT_APPROVED = 'PAYMENT_APPROVED',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    IN_PROGRESS = 'IN_PROGRESS' // 🆕 New status from API
}

export enum EApprovalStatus {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED'
}

export interface UserExaminationAttemptDTO {
    id: string;
    userId: string;
    userEmail: string;
    councilId: string;
    councilName: string;
    attemptNumber: number;
    examSessionId?: string;
    examScheduleTitle?: string;
    status: EAttemptStatus;
    paymentProofId?: string;
    paymentApprovalStatus?: EApprovalStatus;
    nextAttemptAllowedDate?: string;
    createdAt: string;
    updatedAt: string;
}

export interface PaymentProofDTO {
    id: string;
    userId: string;
    userEmail: string;
    councilId: string;
    councilName: string;
    amount: number;
    paymentReference: string;
    proofDocumentUrl?: string;
    submissionDate: string;
    approvalStatus: EApprovalStatus;
    approvedById?: string;
    approvedByEmail?: string;
    approvalDate?: string;
    approvalNotes?: string;
    createdAt: string;
    updatedAt: string;
}

export interface InitiateRetakeRequest {
    councilId: string;
}

export interface PaymentProofUploadRequest {
    amount: number;
    paymentReference: string;
    proofDocument?: File;
}

export interface RetakeEligibilityResponse {
    canRetake: boolean;
    remainingAttempts: number;
    nextAllowedDate?: string;
}

// 🆕 NEW UNIFIED ENDPOINT - Education Program Centric
export interface UnifiedUserExamStatusResponse {
    userId: string;
    userEmail: string;
    educationPrograms: EducationProgramExamStatus[];
    hasAnyEligiblePrograms: boolean;
    totalPrograms: number;
    eligiblePrograms: number;
}

export interface EducationProgramExamStatus {
    educationProgramId: string;
    educationProgramName: string;
    councilId: string;
    councilName: string;
    canRetake: boolean;
    remainingAttempts: number;
    totalAttempts: number;
    maxAllowedAttempts: number;
    nextAllowedDate?: string;
    attempts: UserExaminationAttemptDTO[];
    hasPassingResult: boolean;
    hasActiveAttempt: boolean;
    institutionOffersProgram: boolean;
    institutionValidationMessage: string;
}

export interface ExtendTimeRequest {
    additionalMinutes: number;
    reason?: string;
}

export interface ExtendTimeResponse {
    id: string;
    status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED' | 'COMPLETED';
    additionalTime: number;
    calculatedEndTime: string;
}

export interface ExamSessionAction {
    id: string;
    actionType: 'START' | 'END' | 'BLOCK' | 'EXTEND_TIME' | 'WARNING' | 'TECHNICAL_ISSUE' | 'SUBMIT_ALL_ANSWERS' | 'AUTO_SUBMIT';
    reason: string;
    authorName: string;
    actionTimestamp: string;
    additionalData?: Record<string, any>;
}

export interface EndSessionRequest {
    reason?: string;
}

export interface BlockSessionRequest {
    reason?: string;
}

export interface ResetExamSessionRequest {
    reason: string;
}

export interface SubmitAllAnswersWithReasonRequest {
    answers: SubmitAnswerRequest[];
    reason?: string;
    autoSubmitted?: boolean;
}

export interface ExamAttemptInfo {
    userId: string;
    councilId: string;
    totalAttempts: number;
    maxAttemptsAllowed: number;
    remainingAttempts: number;
    lastAttemptDate?: string;
    lastAttemptResult?: string;
    canTakeExam: boolean;
    nextEligibilityDate?: string;
}


export interface CouncilExamConfigDTO {
  councilId: string;
  waitingPeriodDays: number;
  retakeFeeAmount: number;
  retakeProductCode?: string;
  maxRetakeAttempts: number;
  enabled: boolean;
  
  maxVerificationAttempts: number;
  attendanceTimeWindowMinutes: number;
  preExamAttendanceWindowMinutes: number;  // 🆕 New field for pre-exam attendance window
  postExamStartWindowMinutes: number;      // 🆕 New field for post-exam start window
  submissionGracePeriodMinutes: number;    // 🆕 New field for submission grace period
  supportedLanguages: string[];
  gradingCriteria?: GradingCriteria;       // 🆕 Updated to use structured interface
}

export interface CreateCouncilExamConfigRequest {
  councilId: string;
  waitingPeriodDays: number;
  retakeFeeAmount: number;
  retakeProductCode?: string;
  maxRetakeAttempts: number;
  enabled: boolean;
  
  // New verification settings
  maxVerificationAttempts: number;      // Range: 2-5
  attendanceTimeWindowMinutes: number;  // Range: 30-45
  preExamAttendanceWindowMinutes: number;  // 🆕 New field for pre-exam attendance window (min: 15)
  postExamStartWindowMinutes: number;      // 🆕 New field for post-exam start window (min: 15)
  submissionGracePeriodMinutes: number;    // 🆕 New field for submission grace period (min: 1)
  
  // Language support
  supportedLanguages: string[];
  gradingCriteria?: GradingCriteria;       // 🆕 Updated to use structured interface
}

export interface UpdateCouncilExamConfigRequest extends CreateCouncilExamConfigRequest {}

// Grace Period DTO for API responses
export interface GracePeriodDTO {
  submissionGracePeriodMinutes: number;
}

// Enhanced UserExaminationAttemptDTO with new fields
export interface UserExaminationAttemptDTO {
  id: string; // UUID
  userId: string; // UUID
  userEmail: string;
  councilId: string; // UUID
  councilName: string;
  educationProgramId: string; // 🆕 New field - now required
  educationProgramName: string; // 🆕 New field
  attemptNumber: number;
  examSessionId?: string; // UUID, nullable
  examScheduleTitle?: string; // nullable
  status: EAttemptStatus;
  attemptType: EAttemptType; // New field
  appealReference?: string; // New field, nullable
  paymentProofId?: string; // UUID, nullable
  paymentApprovalStatus?: EApprovalStatus; // nullable
  nextAttemptAllowedDate?: string; // ISO DateTime, nullable
  createdAt: string; // ISO DateTime
  updatedAt: string; // ISO DateTime
}

// Retake initiation response structure
export interface RetakeInitiationResponse {
  attempt: UserExaminationAttemptDTO;
  payment?: {
    transactionId: string;
    invoiceNumber: string;
    paymentLinkUrl: string;
    paymentStatus: string;
    expiresAt: string;
    publicKey: string;
  } | null;
}

// Enhanced PaymentResponse with new payment types (Updated to match API contract)
export interface PaymentResponse {
  transactionId: string;
  invoiceNumber?: string;
  paymentMethod: 'IREMBO_PAY' | 'MANUAL_UPLOAD';
  paymentStatus: 'NEW' | 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
  amount: number; // Decimal
  currency: string; // Default: "RWF"
  paymentLinkUrl?: string; // For IREMBO_PAY
  productCode?: string;
  paymentAccountIdentifier?: string;
  paymentType: EPaymentType; // Using enum
  referenceType?: EPaymentReferenceType; // Using enum
  referenceId?: string; // UUID - links to the referenced entity
  expiresAt?: string; // ISO DateTime
  // Manual payment fields
  manualTransactionNumber?: string;
  receiptFilePath?: string;
  bankId?: string; // UUID
  createdAt: string; // ISO DateTime
  updatedAt: string; // ISO DateTime
}

// New Enums
export enum EAttemptType {
  INITIAL = 'INITIAL',
  RETAKE_PAID = 'RETAKE_PAID',
  RETAKE_APPEAL_GRANTED = 'RETAKE_APPEAL_GRANTED'
}

export enum EPaymentType {
  LICENSE_FEE = 'LICENSE_FEE',
  FINE_PAYMENT = 'FINE_PAYMENT',
  EXAM_RETAKE_FEE = 'EXAM_RETAKE_FEE'
}

export enum EPaymentReferenceType {
  CERTIFICATE_APPLICATION = 'CERTIFICATE_APPLICATION',
  FINE = 'FINE',
  EXAM_RETAKE = 'EXAM_RETAKE'
}

// API Request interfaces for new endpoints
export interface InitiateRetakePaymentRequest {
  userId: string;
  councilId: string;
  educationProgramId: string;    // 🆕 Required for validation
  attemptId: string;
  paymentMethod: "IREMBO_PAY" | "MANUAL_UPLOAD";
  manualTransactionNumber?: string;  // Required for manual payments
  bankId?: string;                   // Required for manual payments
}

export interface InitiateAppealGrantedRetakeRequest {
  userId: string;
  councilId: string;
  educationProgramId: string;  // 🆕 New required field
  appealReference: string;
}

// API Response interfaces
export interface RetakePaymentResponse {
  success: boolean;
  message: string;
  data: PaymentResponse;
}

export interface AppealGrantedRetakeResponse {
  success: boolean;
  message: string;
  data: UserExaminationAttemptDTO;
}

// Enhanced API Response interfaces for standardized format
export interface StandardApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  status: string;
  timestamp: string;
}

// 🆕 Exam Access Validation Interface
export interface ExamAccessCheckRequest {
  educationProgramId: string;
}

export interface ExamAccessCheckResponse {
  hasAccess: boolean;
  educationProgramId: string;
  certificateApplicationId?: string;
  applicationStatus?: string;
  approvedAt?: string;
}

// 🆕 Certificate Application Validation Interface
export interface UserApplicationByEducationProgramResponse {
  applicationId: string;
  serviceId: string;
  serviceName: string;
  educationProgramId: string;
  educationProgramName: string;
  status: string;
  createdAt: string;
  approvedAt?: string;
}

export interface StandardErrorResponse {
  success: false;
  message: string;
  errors: string[];
  status: string;
  timestamp: string;
}

// 🆕 Education Program Validation Error Types
export enum EducationProgramErrorCode {
  DUPLICATE_EDUCATION_PROGRAM_APPLICATION = "DUPLICATE_EDUCATION_PROGRAM_APPLICATION",
  MISSING_EDUCATION_PROGRAM = "MISSING_EDUCATION_PROGRAM",
  MISSING_EDUCATION_PROGRAM_APPROVAL = "MISSING_EDUCATION_PROGRAM_APPROVAL",
  MISSING_EDUCATION_PROGRAM_ASSIGNMENT = "MISSING_EDUCATION_PROGRAM_ASSIGNMENT",
  INVALID_EDUCATION_PROGRAM_INSTITUTION = "INVALID_EDUCATION_PROGRAM_INSTITUTION",
  EXISTING_PENDING_PAYMENT = "EXISTING_PENDING_PAYMENT"
}

export interface EducationProgramValidationError {
  success: false;
  message: string;
  errorCode: EducationProgramErrorCode;
  timestamp: string;
  path: string;
  details?: {
    [key: string]: any;
  };
}


export interface ExamCenter {
    id: string;
    name: string;
    code: number; // Auto-generated or manually provided unique numeric code
    description?: string;
    location: string;
    active: boolean;
    districtId?: number;
    councilId?: string;
    districtName?: string;
    councilName?: string;
    totalSeats?: number;
    remainingSeats?: number;
    activeSubCenters?: ExamSubCenterWithInvigilator[];
    subCenters?: ExamSubCenter[];
    createdAt?: string;
    updatedAt?: string;
}

export interface CreateExamCenterRequest {
    name: string;
    code?: number | null; // Optional: Auto-generated if not provided
    description?: string;
    location: string;
    active: boolean;
    districtId: number | string; // Allow both number and string for form handling
    councilId: string;
}

export interface UpdateExamCenterRequest extends CreateExamCenterRequest {
    id: string;
}

// Invigilator Shift Types
export interface InvigilatorShift {
    id: string;
    invigilatorId: string;
    invigilatorName: string;
    invigilatorEmail: string;
    examCenterId: string;
    examCenterName: string;
    examCenterCode?: string;
    examSubCenterId?: string | null;
    examSubCenterName?: string | null;
    shiftType: 'CENTER' | 'ROOM';
    scope: string;
    shiftDate: string;
    shiftStartTime: string;
    shiftEndTime: string;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

export type InvigilatorShiftStatus = 'SCHEDULED' | 'ACTIVE' | 'COMPLETED' | 'NO_SHOW' | 'CANCELLED';

export interface CreateInvigilatorShiftRequest {
    invigilatorId: string;
    examCenterId: string;
    examSubCenterId?: string | null;
    shiftDate: string;
    shiftStartTime: string;
    shiftEndTime: string;
    notes?: string;
}

export interface UpdateInvigilatorShiftRequest {
    invigilatorId?: string;
    examCenterId?: string;
    examSubCenterId?: string | null;
    shiftDate?: string;
    shiftStartTime: string;
    shiftEndTime: string;
    notes?: string;
}

// Exam Sub-Center Daily State Types
export interface ExamSubCenterDailyState {
    id: string;
    examSubCenterId: string;
    examSubCenterName: string;
    stateDate: string;
    examInitiated: boolean;
    examInitiatedAt?: string;
    examScheduleCode?: string; // 6-digit code
    codeGeneratedAt?: string;
    codeGeneratedBy?: IUser;
    assignedInvigilator?: IUser; // Room invigilator
    createdAt: string;
    updatedAt: string;
}

export interface ExamScheduleCenter {
    examScheduleId: string;
    examScheduleTitle: string;
    examCenterId: string;
    examCenterName: string;
    examCenterCode: string;
    examCenterLocation: string;
    totalSubCenters: number;
    totalSeats: number;
    createdAt: string;
    updatedAt: string;
}

export interface ExamSubCenter {
    id: string;
    name: string;
    seats: number;
    currentOccupancy?: number; // Current number of assigned students (shared across all schedules)
    active: boolean;
    customStartDate?: string;
    customStartTime?: string;
    examCenterId: string;
    examCenterName?: string;
    remainingSeats?: number;
    activeSessions?: number;
    createdAt: string;
    updatedAt: string;
}

// Extended ExamSubCenter with invigilator assignment data (used in exam schedule responses)
export interface ExamSubCenterWithInvigilator extends ExamSubCenter {
    assignedInvigilator?: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        username: string;
        profilePicture?: string | null;
        gender: string;
        nationalID: string;
        citizenType: string;
        roles: Array<{
            roleId: string;
            roleName: string;
            code: string;
        }>;
        accountStatus: string;
        visibility: string;
        securityAttempt?: any;
    } | null;
    invigilatorAssignedAt?: string;
    invigilatorAssignedBy?: {
        id: string;
        email: string;
        firstName: string;
        lastName: string;
        username: string;
        profilePicture?: string | null;
        gender: string;
        nationalID: string;
        citizenType: string;
        roles: Array<{
            roleId: string;
            roleName: string;
            code: string;
        }>;
        accountStatus: string;
        visibility: string;
        securityAttempt?: any;
    } | null;
}

export interface CreateExamSubCenterRequest {
    name: string;
    seats: number;
    active: boolean;
    customStartDate?: string;
    customStartTime?: string;
    examCenterId: string;
}

export interface UpdateExamSubCenterRequest extends CreateExamSubCenterRequest {
    id: string;
}

// New interfaces for Education Institution and Certificate Application enhancements
export interface EducationInstitution {
    id: string;
    name: string;
    type: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface CertificateApplicationDTO {
    id: string;
    certificateAmount: number;
    transactionNumber?: string;
    exitAward: string;
    userId: string;
    councilId: string;
    serviceId: string;
    
    // 🆕 New Education Program Related Fields (Required for "EXAMINATION" service)
    diplomasFileId?: string;           // UUID reference to uploaded diploma files
    educationInstitutionId?: string;   // UUID reference to education institution  
    educationProgramId?: string;       // UUID reference to education program
    
    // Nested objects
    educationInstitution?: EducationInstitution;
    educationProgram?: EducationProgram;
    
    createdAt: string;
    updatedAt: string;
}

export interface CreateCertificateApplicationRequest {
    certificateAmount: number;
    transactionNumber?: string;
    exitAward: string;
    userId: string;
    councilId: string;
    serviceId: string;
    
    // 🆕 New fields for EXAMINATION service
    diplomasFileId?: string;
    educationInstitutionId?: string;
    educationProgramId?: string;
}

export interface OverrideEducationDetailsRequest {
    educationInstitutionId: string;
    educationProgramId: string;
    overrideReason: string;
}

export interface OverrideEducationDetailsResponse {
    applicationId: string;
    previousEducationInstitution: EducationInstitutionDTO;
    newEducationInstitution: EducationInstitutionDTO;
    previousEducationProgram: EducationProgram;
    newEducationProgram: EducationProgram;
    overriddenBy: string;
    overriddenByName: string;
    overriddenAt: string;
    overrideReason: string;
    approverComments: string;
    approvalLevel: string;
    approvalDetailId: string;
}

// Update Application Year (Level 1 - First Reviewer)
export interface UpdateApplicationYearRequest {
    yearofLicence: number;
}

export interface UpdateApplicationYearResponse {
    success: boolean;
    message: string;
    data: {
        applicationId: string;
        yearofLicence: number;
        licenceDate: string;
        licenceExpiredDate: string;
        updatedBy: string;
        updatedByName: string;
        approvalLevel: number;
        message: string;
    };
}

// Override Application Dates (Level 4 - Approver)
export interface OverrideApplicationDatesRequest {
    licenceDate: string;
    licenceExpiredDate: string;
    overrideReason: string;
}

export interface OverrideApplicationDatesResponse {
    success: boolean;
    message: string;
    data: {
        applicationId: string;
        licenceDate: string;
        licenceExpiredDate: string;
        previousLicenceDate: string;
        previousLicenceExpiredDate: string;
        updatedBy: string;
        updatedByName: string;
        updateReason: string;
        approvalLevel: number;
        message: string;
    };
}

// Reset Licence Dates to NULL (First Reviewer Level 1 or Approver Level 4)
export interface ResetLicenceDatesResponse {
    success: boolean;
    message: string;
    data: {
        applicationId: string;
        updatedBy: string;
        updatedByName: string;
        resetReason: string;
        approvalLevel: number;
    };
}

// 🆕 Enhanced Practical Exam Results Types
export interface PracticalExamResultDTO {
  id: string;
  examSessionId: string;
  examSessionCode: string; // 🆕 Exam session code for practical results
  examCode?: string; // 🆕 Exam code for practical results
  examScheduleTitle: string;
  userId: string;
  userEmail: string;
  userName: string;
  educationProgramId: string;
  educationProgramName: string;
  councilId: string;
  councilName: string;
  scorePercentage: number;
  published: boolean;
  recordedAt: string;
  recordedBy: string;
  recordedByName: string;
  notes?: string;
}

export interface AddPracticalResultRequest {
  scorePercentage: number;
  notes?: string;
}

export interface UpdatePracticalResultRequest {
  scorePercentage: number;
  notes?: string;
}

export interface CanDeletePracticalResultResponse {
  canDelete: boolean;
  practicalResultId: string;
}

// Bulk Import Types
export interface BulkQuestionUnitDTO {
  name: string;
  code: string;
  description?: string;
  active?: boolean;
  questionModuleCode: string;
  bloomLevelIds?: string[];
}

export interface BulkQuestionModuleDTO {
  name: string;
  code: string;
  description?: string;
  active?: boolean;
  educationProgramCode: string;
  questionUnits: BulkQuestionUnitDTO[];
}

export interface BulkEducationProgramDTO {
  name: string;
  code: string;
  description?: string;
  educationLevel: string;
  supportsPracticalExams?: boolean;
  active?: boolean;
  councilId: string;
  institutionIds?: string[]; // 🆕 Institution IDs that offer this program
  questionModules: BulkQuestionModuleDTO[];
}

export interface BulkImportRequestDTO {
  educationPrograms: BulkEducationProgramDTO[];
}

export interface BulkImportResultDTO {
  success: boolean;
  message: string;
  totalEducationPrograms: number;
  successfulEducationPrograms: number;
  totalQuestionModules: number;
  successfulQuestionModules: number;
  totalQuestionUnits: number;
  successfulQuestionUnits: number;
  errors: string[] | null;
}

// 🆕 Grading Flow Types (Updated for Weighted Scoring)
export interface GradingCriteriaDTO {
  passing_percentage: number;
  min_theory_percentage: number;     // Renamed from min_session_percentage
  min_practical_percentage: number;
  theory_weight: number;            // Weight for theory component (0.0-1.0)
  practical_weight: number;          // Weight for practical component (0.0-1.0)
  allow_average_exception: boolean; // Whether to allow manual approval exceptions
  // Deprecated field - kept for backward compatibility
  requires_both_pass?: boolean;     // DEPRECATED: No longer used in weighted scoring
}


export interface ComputeResultRequest {
  sessionId: string;
}

export interface ComputeResultResponse {
  success: boolean;
  message: string;
  data: {
    examResult: ExamResultDTO;
    published: boolean;
    requiresPractical: boolean;
  };
}

export interface PublishFinalResultRequest {
  sessionId: string;
}

export interface PublishFinalResultResponse {
  success: boolean;
  message: string;
  data: {
    examResult: ExamResultDTO;
    practicalResult?: PracticalExamResultDTO;
    combinedScore: number;
    passed: boolean;
    gradingCriteria: GradingCriteriaDTO;
  };
}

export interface CalculatePassStatusRequest {
  sessionId: string;
}

export interface CalculatePassStatusResponse {
  success: boolean;
  message: string;
  data: {
    passed: boolean;
    sessionScore: number;
    practicalScore?: number;
    combinedScore?: number;
    gradingCriteria: GradingCriteriaDTO;
    individualChecks: {
      sessionPassed: boolean;
      practicalPassed?: boolean;
    };
    combinedCheck: boolean;
  };
}

// 🆕 Enhanced Unified Exam Result Response Types
export interface UnifiedExamResultResponse {
  success: boolean;
  message: string;
  data: ExamResultDTO;
}

export interface AddPracticalResultResponse {
  success: boolean;
  message: string;
  data: {
    practicalResult: PracticalExamResultDTO;
    finalResult: {
      finalScorePercentage: number;
      passed: boolean;
      finalPublished: boolean;
      combinedScoring: {
        sessionScore: number;
        practicalScore: number;
        combinedScore: number;
        gradingMethod: string;
      };
    };
    notifications: {
      emailSent: boolean;
      recipientEmail: string;
    };
  };
}

export interface CanDeletePracticalResultResponse {
  success: boolean;
  message: string;
  data: {
    canDelete: boolean;
    practicalResultId: string;
  };
}

// 🆕 Enhanced User Exam Results Response
export interface UserExamResultsResponse {
  success: boolean;
  message: string;
  data: {
    userId: string;
    userEmail: string;
    totalResults: number;
    pendingPracticalResults: number;
    finalizedResults: number;
    results: ExamResultDTO[];
  };
}

// 🆕 Exam Retake Request Types
export enum ERetakeRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  APPROVED_WITH_DATE = 'APPROVED_WITH_DATE',
  REJECTED = 'REJECTED'
}

export interface ExamRetakeRequestDTO {
  id: string;
  userId: string;
  userEmail: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  educationProgramId: string;
  educationProgramName: string;
  requestReason: string;
  supportingDocumentPath?: string;
  requestStatus: ERetakeRequestStatus;
  submissionDate: string;
  reviewedById?: string;
  reviewedByEmail?: string;
  reviewDate?: string;
  reviewNotes?: string;
  scheduledDate?: string;
  additionalAttemptsGranted: number;
  createdAt: string;
  updatedAt: string;
}

export interface SubmitRetakeRequestRequest {
  educationProgramId: string;
  requestReason: string;
  supportingDocument?: File;
}

export interface ReviewRetakeRequestRequest {
  requestStatus: ERetakeRequestStatus;
  reviewNotes?: string;
  scheduledDate?: string;
  additionalAttemptsGranted: number;
}

export interface RetakeRequestEligibilityResponse {
  canSubmit: boolean;
  message?: string;
}

export interface RetakeRequestSummaryDTO {
  id: string;
  educationProgramName: string;
  requestStatus: ERetakeRequestStatus;
  submissionDate: string;
  additionalAttemptsGranted: number;
}

export interface PendingRetakeRequestDTO {
  id: string;
  userId: string;
  userEmail: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
    profilePicture?: string;
    gender?: string;
    nationalID?: string;
    citizenType?: string;
  };
  educationProgramId: string;
  educationProgramName: string;
  councilId: string;
  councilName: string;
  requestReason: string;
  submissionDate: string;
  supportingDocumentPath?: string;
  requestStatus: ERetakeRequestStatus;
  reviewedBy?: string;
  reviewedByEmail?: string;
  reviewDate?: string;
  reviewNotes?: string;
  scheduledDate?: string;
  additionalAttemptsGranted: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdditionalAttemptsResponse {
  success: boolean;
  message: string;
  data: number;
}

// 🆕 New types for Invigilator Exam Initiation
export interface InitiateExamRequest {
  examSubCenterId: string;
}

export interface InitiateExamResponse {
  examScheduleCode: string;
  initiatedAt: string;
  examTitle: string;
  subCenterName: string;
}

export interface StartExamSessionWithCodeRequest {
  examScheduleCode: string;
}

// Enhanced ExamSession with initiation status
export interface ExamSessionWithInitiation extends ExamSession {
  examInitiated?: boolean;
  examScheduleCode?: string;
  initiatedAt?: string;
  initiatedBy?: string;
  initiatedByName?: string;
}

// Enhanced ExamSessionDTO with initiation status
export interface ExamSessionDTOWithInitiation extends ExamSessionDTO {
  examInitiated?: boolean;
  examScheduleCode?: string;
  initiatedAt?: string;
  initiatedBy?: string;
  initiatedByName?: string;
}

// 🆕 Invigilator Assignment Types
export interface InvigilatorUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface SubCenterInvigilatorAssignments {
  [subCenterId: string]: string; // subCenterId -> invigilatorId
}

export interface ExamScheduleSubCenter {
  examScheduleId: string;
  examSubCenterId: string;
  assignedInvigilator: InvigilatorUser | null;
  invigilatorAssignedAt: string | null; // ISO 8601 timestamp
  invigilatorAssignedBy: InvigilatorUser | null;
  customStartDate: string | null; // YYYY-MM-DD
  customStartTime: string | null; // HH:mm:ss
  examScheduleCode: string | null;
  examInitiated: boolean;
  examInitiatedAt: string | null; // ISO 8601 timestamp
}

// Enhanced ExamSchedule with invigilator assignments
export interface ExamScheduleWithInvigilators extends ExamSchedule {
  subCenterInvigilatorAssignments?: SubCenterInvigilatorAssignments;
  examScheduleSubCenters?: ExamScheduleSubCenter[];
}

// Enhanced CreateExamScheduleRequest with invigilator assignments
export interface CreateExamScheduleWithInvigilatorsRequest extends CreateExamScheduleRequest {
  subCenterInvigilatorAssignments?: SubCenterInvigilatorAssignments;
}

// Enhanced UpdateExamScheduleRequest with invigilator assignments
export interface UpdateExamScheduleWithInvigilatorsRequest extends UpdateExamScheduleRequest {
  subCenterInvigilatorAssignments?: SubCenterInvigilatorAssignments;
}

// Invigilator Assignment API Request/Response Types
export interface AssignInvigilatorRequest {
  invigilatorIds: string[];
}

export interface AssignInvigilatorResponse {
  examScheduleId: string;
  examSubCenterId: string;
  assignedInvigilator: InvigilatorUser;
  invigilatorAssignedAt: string;
  invigilatorAssignedBy: InvigilatorUser;
}

export interface RemoveInvigilatorAssignmentResponse {
  examScheduleId: string;
  examSubCenterId: string;
  assignedInvigilator: null;
}

// Enhanced InitiateExamRequest (already exists but adding to this section)
export interface InitiateExamRequest {
  examSubCenterId: string;
}

export interface InitiateExamResponse {
  examScheduleCode: string;
  examInitiatedAt: string;
  examTitle: string;
  subCenterName: string;
}

// Invigilator Dashboard Types
export interface InvigilatorAssignment {
  examScheduleId: string;
  examScheduleTitle: string;
  examSubCenterId: string;
  examSubCenterName: string;
  examCenterName: string;
  startDate: string;
  startTime: string;
  duration: number;
  examInitiated: boolean;
  examInitiatedAt: string | null;
  examScheduleCode: string | null;
  totalSessions: number;
  activeSessions: number;
  completedSessions: number;
}

export interface InvigilatorDashboardData {
  invigilatorId: string;
  invigilatorName: string;
  invigilatorEmail: string;
  assignments: InvigilatorAssignment[];
  totalAssignments: number;
  activeAssignments: number;
  upcomingAssignments: number;
}

// New types for the invigilator API endpoints
export interface InvigilatorExamCenterWithSubCentersDTO {
  id: string;
  name: string;
  description: string | null;
  location: string;
  active: boolean;
  districtName: string | null;
  activeSubCenters: InvigilatorExamSubCenterDTO[];
}

export interface InvigilatorExamSubCenterDTO {
  id: string;
  name: string;
  seats: number;
  active: boolean;
  examCenterId: string;
  examCenterName: string;
  assignedInvigilator: InvigilatorUserBasicDTO | null;
  invigilatorAssignedAt: string | null;
  invigilatorAssignedBy: InvigilatorUserBasicDTO | null;
}

export interface InvigilatorExamSessionDTO {
  id: string;
  examCode: string;
  status: InvigilatorSessionStatus;
  user: InvigilatorUserBasicDTO;
  examSchedule: InvigilatorExamScheduleBasicDTO;
  examSubCenter: InvigilatorExamSubCenterDTO;
  language: InvigilatorLanguageDTO | null;
  ipAddress: string | null;
  deviceInfo: string | null;
  startedAt: string | null;
  endedAt: string | null;
  additionalTime: number;
  autoSubmitted: boolean;
  deviceVerified: boolean;
  deviceVerifiedAt: string | null;
  attendanceVerified: boolean;
  attendanceVerifiedBy: InvigilatorUserBasicDTO | null;
  attendanceVerifiedAt: string | null;
  attendanceVerifiedByEmail?: string | null;
  verificationAttempts?: number;
  maxVerificationAttempts?: number;
  examSubCenterId?: string;
  examSubCenterName?: string;
}

export interface InvigilatorUserBasicDTO {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName?: string;
}

export interface InvigilatorExamScheduleBasicDTO {
  id: string;
  title: string;
  startDate: string;
  startTime: string;
  duration: number;
}

export interface InvigilatorLanguageDTO {
  id: string;
  code: string;
  name: string;
}

export enum InvigilatorSessionStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  ENDED = "ENDED",
  BLOCKED = "BLOCKED",
  EXPIRED = "EXPIRED",
  COMPLETED = "COMPLETED"
}

// Batch Operations Types
export interface MoveRoomRequest {
  targetSubCenterId: string;
  reason: string;
}

export interface MoveRoomResponse {
  sessionId: string;
  examCode: string;
  studentName: string;
  studentEmail: string;
  oldSubCenterId: string;
  oldSubCenterName: string;
  newSubCenterId: string;
  newSubCenterName: string;
  reason: string;
  requiresReVerification: boolean;
}

export interface BatchResumeRequest {
  sessionIds: string[];
  additionalMinutes?: number;
  reason: string;
}

export interface BatchResumeResult {
  sessionId: string;
  examCode: string;
  studentName: string;
  studentEmail: string;
  success: boolean;
  errorMessage?: string;
  additionalMinutesAdded?: number;
  wasExpired?: boolean; // 🆕 true if session was EXPIRED when resumed (submission-only mode)
  isRecoverable?: boolean; // 🆕 true if session can still submit (always true if resume succeeded)
}

export interface BatchResumeResponse {
  results: BatchResumeResult[];
  totalSessions: number;
  successCount: number;
  failureCount: number;
  reason: string;
}

export interface BatchExtendTimeRequest {
  subCenterId: string;
  examDate: string;
  additionalMinutes: number;
  reason: string;
}

export interface BatchExtendTimeResponse {
  sessionsAffected: number;
  sessionIds: string[];
  additionalMinutesAdded: number;
  reason: string;
  subCenterId: string;
  subCenterName: string;
}

// 🆕 Batch Mark Attendance Types
export interface BatchMarkAttendanceRequest {
  sessionIds: string[];
  reason: string;
  comments?: string;
}

export interface BatchMarkAttendanceResult {
  sessionId: string;
  examCode: string;
  studentName: string;
  studentEmail: string;
  success: boolean;
  errorMessage?: string;
}

export interface BatchMarkAttendanceResponse {
  results: BatchMarkAttendanceResult[];
  successCount: number;
  failureCount: number;
  reason: string;
}

// SSE Event Types for Batch Operations
export interface BatchOperationSSEEvent {
  eventType: 'ROOM_MOVED' | 'SESSION_RESUMED' | 'TIME_EXTENDED';
  examSessionId: string;
  actionType: 'MOVE_ROOM' | 'RESUME_SESSION' | 'EXTEND_TIME';
  authorId: string;
  authorName: string;
  reason: string;
  additionalData: string;
  timestamp: string;
  payload: any; // ExamSessionDTO
}

// Assignment Conflict Types
export interface AssignmentConflict {
  invigilatorId: string;
  invigilatorName: string;
  conflictingScheduleId: string;
  conflictingScheduleTitle: string;
  conflictingSubCenterId: string;
  conflictingSubCenterName: string;
  conflictingStartDate: string;
  conflictingStartTime: string;
  conflictingEndTime: string;
  gapMinutes: number;
  isConflict: boolean;
}

export interface AssignmentValidationResult {
  isValid: boolean;
  conflicts: AssignmentConflict[];
  warnings: string[];
  errors: string[];
}

// 🆕 Exam Session Language Types
export interface ExamSessionLanguage {
  id: string;
  code: string;
  name: string;
  nativeName: string;
  enabled: boolean;
}

export interface UpdateExamSessionLanguageRequest {
  languageId: string;
}

export interface UpdateExamSessionLanguageResponse {
  id: string;
  status: 'PENDING' | 'ACTIVE' | 'ENDED' | 'EXPIRED' | 'BLOCKED';
  languageId: string;
  languageCode: string;
  languageName: string;
}

// New DTOs for enhanced exam schedule functionality
export interface QuestionSelectionRequestDTO {
  moduleSelections?: ModuleSelectionDTO[];
  unitSelections?: UnitSelectionDTO[];
}

export interface ModuleSelectionDTO {
  moduleId: string;
  questionCount: number;
  bloomsDistribution?: { [key: number]: number };
  bloomsLevelId?: string;
}

export interface UnitSelectionDTO {
  unitId: string;
  questionCount: number;
  bloomsDistribution?: { [key: number]: number };
  bloomsLevelId?: string;
}

// 🆕 Batch Exam Results Import Types
export interface ManualExamScoreEntryDTO {
  examCode: string;        // Required: Unique exam session code
  correctAnswers: number;  // Required: Number of correct answers (>= 0)
  notes?: string;         // Optional: Notes about the manual entry
}

export interface BatchManualExamScoreRequest {
  entries: ManualExamScoreEntryDTO[];
}

export interface ManualExamScoreResult {
  examCode: string;
  success: boolean;
  errorMessage: string | null;
  scorePercentage: number;
  overriddenExistingResult: boolean;
}

export interface BatchManualExamScoreResponse {
  success: boolean;
  message: string;
  data: {
    successCount: number;
    totalCount: number;
    results: ManualExamScoreResult[];
  };
}

export interface BatchPracticalExamScoreEntryDTO {
  examCode: string;           // Required: Unique exam session code
  scorePercentage: number;     // Required: Score percentage (0.00 - 100.00)
  notes?: string;             // Optional: Notes about the practical exam
}

export interface BatchPracticalExamScoreRequest {
  entries: BatchPracticalExamScoreEntryDTO[];
}

export interface PracticalExamScoreImportResult {
  examCode: string;
  success: boolean;
  errorMessage: string | null;
  scorePercentage: number;
  finalPublished: boolean;
}

export interface BatchPracticalExamScoreResponse {
  success: boolean;
  message: string;
  data: {
    successCount: number;
    totalCount: number;
    results: PracticalExamScoreImportResult[];
  };
}

// 🆕 Batch Exam Result Processing Types
export interface BatchComputationStatusResponse {
  success: boolean;
  message: string;
  status: number;
  data: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  };
}

export interface FailedComputationDTO {
  id: string;
  sessionId: string;
  userName: string;
  computationAttemptCount: number;
  lastComputationError: string;
  createdAt: string;
}

export interface FailedComputationsResponse {
  success: boolean;
  message: string;
  status: number;
  data: FailedComputationDTO[];
  meta: {
    pagination: {
      totalElements: number;
      totalPages: number;
      last: boolean;
      size: number;
      number: number;
      first: boolean;
    };
  };
}

export interface RetryFailedResponse {
  success: boolean;
  message: string;
  status: number;
  data: {
    retriedCount: number;
  };
}